/**
 * Manufacturer Detail Component
 *
 * Read-only detail view for a single manufacturer.
 * Mirrors the OrganizationDetail look: back-button row with an Edit button,
 * a dark ProfileHeader hero, and dark ProfileSection cards.
 */

import { Link, useNavigate } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ProfileHeader, ProfileSection } from '@/components/sensnet/ProfileLayout'
import { ArrowLeft, Loader2, Edit, Factory, Briefcase } from 'lucide-react'
import { useApiQuery } from '@/api/ApiQueryServiceProvider'

export interface ManufacturerDetailProps {
  manufacturerNumber: number
}

export function ManufacturerDetail({ manufacturerNumber }: ManufacturerDetailProps) {
  const navigate = useNavigate()

  const { data, isLoading } = useApiQuery<any>('getDeviceManufacturer', { manufacturerNumber })
  const manufacturer = data?.content?.manufacturer

  const handleBack = () => navigate({ to: '/sensory/manufacturers' })

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading manufacturer details...</span>
        </div>
      </div>
    )
  }

  if (!manufacturer) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Manufacturer not found.</p>
        <Button variant="outline" onClick={handleBack}>Back to Manufacturers</Button>
      </div>
    )
  }

  const isArchived = !!manufacturer.archiveDate

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={handleBack} className="gap-2 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link
            to="/sensory/manufacturers/$manufacturerNumber/edit"
            params={{ manufacturerNumber: String(manufacturer.manufacturerNumber) }}
          >
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={manufacturer.manufacturerName}
        subtitle={`Manufacturer Number: ${manufacturer.manufacturerNumber}`}
        subtitleIcon={<Factory className="h-4 w-4" />}
        badge={isArchived ? 'Archived' : 'Active'}
      />

      <ProfileSection title="General Information" icon={<Briefcase className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <div>
            <p className="text-xs text-muted-foreground">Manufacturer Name</p>
            <p className="text-sm">{manufacturer.manufacturerName}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Manufacturer Number</p>
            <p className="text-sm">{manufacturer.manufacturerNumber}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Status</p>
            <p className="text-sm">{isArchived ? 'Archived' : 'Active'}</p>
          </div>
        </div>
      </ProfileSection>
    </div>
  )
}
